/**
 * SAB-813: ONE availability designation per player, from TWO independent columns.
 *
 * ── THE TWO SIGNALS ─────────────────────────────────────────────────────────
 *
 *   nfl_players.status         roster state, from fetch-nfl-rosters (SportRadar full_roster)
 *                              live now: ACT 3140, IR 134, PUP 31, IRD 8, NON 8, EXE 6, UFA 1
 *   nfl_players.injury_status  weekly injury report, from sync-nfl-player-status
 *                              NULL for all 3,328 players until REG week 1
 *
 * They are not duplicates -- one says "is he on the roster at all", the other says "is he
 * playing Sunday" -- but a manager scanning a draft board does not care about the
 * distinction. They care whether the player plays. So the row gets one marker.
 *
 * ── PRECEDENCE, AND WHY THIS ORDER ──────────────────────────────────────────
 *
 * injury_status wins when set and meaningful; status is the fallback. That is not a
 * preference, it is what makes the marker switch source by itself at REG week 1:
 *
 *   preseason (today)  injury_status NULL everywhere  -> the pill shows the roster state
 *   from REG week 1    the weekly report populates    -> the pill shows the game status
 *
 * The common in-season case is `status = ACT` with `injury_status = Out` -- rostered, but
 * ruled out for the game. Reading status first would show nothing at all for that player,
 * which is the exact failure this ticket exists to prevent.
 *
 * ── NOTHING IS EVER SILENTLY DROPPED ────────────────────────────────────────
 *
 * An unmapped value renders RAW rather than disappearing. This matters more than it looks:
 * both pre-existing pills in this codebase key off closed maps, and prod carries values
 * neither map covers. `IRD` (8 players, injured reserve - designated to return) is in the
 * live data right now and is in nobody's label list, including this file's. It renders as
 * "IRD". A designation we have not seen before is still information; a blank cell is a lie.
 *
 * The tone for an unmapped value is NEUTRAL, not severe. Colouring an unrecognised string
 * red would over-warn on something like `UFA`; the label itself carries the meaning and the
 * reader can see it verbatim. This is a deliberate trade and the reason the label is never
 * truncated away.
 */

/** How loudly the designation reads. Maps to colour in the components, nowhere else. */
export type AvailabilityTone = 'out' | 'caution' | 'neutral';

export interface Availability {
  /** Short label for the row pill: IR, PUP, O, Q ... or the raw value when unmapped. */
  label: string;
  /** Full designation, for the sheet strip, `title` and screen readers. */
  full: string;
  tone: AvailabilityTone;
  /** Which column this came from. Lets a surface show provenance; not used for colour. */
  source: 'injury_status' | 'status';
}

/**
 * Values of `injury_status` that mean "no designation". The weekly feed has never run in
 * prod, so this list is defensive: SportRadar's measured vocabulary is Out / Questionable /
 * Doubtful / Unknown, but both shipped components already guard Active/Healthy and there is
 * no cost to keeping that.
 */
const INJURY_NOT_A_DESIGNATION = new Set(['active', 'healthy']);

/** `status` value that means "nothing to say". Every other roster state is worth showing. */
const STATUS_HEALTHY = 'act';

/**
 * Label + tone by normalised value. Deliberately covers BOTH vocabularies in one table:
 * the two columns feed one pill, so two tables could disagree about the same string.
 *
 * `pup` appears once, here, and is reachable only from `status`. NFLPlayersPage has a `PUP`
 * branch on its injury_status map that can never fire -- PUP is a roster state and the
 * weekly feed does not emit it. That dead branch is the evidence that these two signals
 * have been conflated before, which is why they share a table now rather than a convention.
 */
const KNOWN: Record<string, { label: string; tone: AvailabilityTone }> = {
  // Weekly injury report (game status)
  out: { label: 'O', tone: 'out' },
  doubtful: { label: 'D', tone: 'caution' },
  questionable: { label: 'Q', tone: 'caution' },
  // Roster state
  ir: { label: 'IR', tone: 'out' },
  'injured reserve': { label: 'IR', tone: 'out' },
  // SAB-964: IRD is injured reserve DESIGNATED TO RETURN -- 8 live prod players, and the
  // designation Sleeper shows simply as "IR". It is deliberately NOT collapsed into `ir`:
  // a manager drafting in September cares a great deal whether a player is out for the
  // season or eligible to come back, and that is the entire difference between the codes.
  ird: { label: 'IR-R', tone: 'out' },
  // UFA renders as UFA, NOT as "FA". The shorter label collided with the OWNERSHIP badge
  // on the mobile players card, which already reads "FA" for a player nobody has drafted:
  // one card showed "Kyler Murray FA QB MIN FA", two identical labels meaning
  // "not on an NFL roster" and "available in this league". Measured, then reverted.
  //
  // Neutral rather than red: this is a roster-MEMBERSHIP state, not a health one, and
  // colouring it like a season-ending injury would be a false alarm.
  ufa: { label: 'UFA', tone: 'neutral' },
  pup: { label: 'PUP', tone: 'out' },
  non: { label: 'NON', tone: 'out' },
  exe: { label: 'EXE', tone: 'out' },
  sus: { label: 'SUS', tone: 'out' },
  suspended: { label: 'SUS', tone: 'out' },
};

function designate(raw: string, source: Availability['source']): Availability {
  const hit = KNOWN[raw.trim().toLowerCase()];
  return hit
    // The RAW value is kept as `full` even when mapped, so the sheet and the tooltip say
    // "Questionable" while the row says "Q". Nothing has to re-expand an abbreviation.
    ? { label: hit.label, full: raw.trim(), tone: hit.tone, source }
    : { label: raw.trim(), full: raw.trim(), tone: 'neutral', source };
}

/**
 * The single decision point. Returns null when the player has nothing worth marking --
 * healthy and on the active roster -- which is the overwhelmingly common case and must
 * render NOTHING rather than an "OK" badge on 3,140 rows.
 */
export function resolveAvailability(
  injuryStatus: string | null | undefined,
  status: string | null | undefined,
): Availability | null {
  const injury = injuryStatus?.trim();
  if (injury && !INJURY_NOT_A_DESIGNATION.has(injury.toLowerCase())) {
    return designate(injury, 'injury_status');
  }
  const roster = status?.trim();
  if (roster && roster.toLowerCase() !== STATUS_HEALTHY) {
    return designate(roster, 'status');
  }
  return null;
}

/**
 * ── SAB-965: IR-SLOT ELIGIBILITY ────────────────────────────────────────────
 *
 * Lives here, beside the pill, so the rule a manager is told and the rule the app
 * enforces come out of one vocabulary. Before this, eligibility read `injury_status`
 * alone -- NULL for all 3,328 players -- so NO player was IR-eligible: a manager who
 * drafted someone on IR, IRD or PUP could not stash him.
 *
 * ── WHY THIS IS AN OR OVER THE TWO COLUMNS, NOT THE PILL'S SINGLE ANSWER ────
 *
 * `resolveAvailability` deliberately picks ONE designation, injury report first, because a
 * row has room for one marker. Eligibility is a different question and must not inherit
 * that precedence.
 *
 * The case that separates them: `status = 'IR'` with `injury_status = 'Questionable'`.
 * The pill correctly shows Q -- that is this week's game status, the more useful thing to
 * read. But the player is on injured reserve, and he is IR-slot eligible. Resolving to a
 * single value first would refuse him, on the strength of a designation that says nothing
 * about his roster state.
 *
 * So: eligible if the ROSTER state qualifies, OR if the injury report says Out.
 *
 * Doubtful and Questionable are NOT eligible -- they are week-to-week game statuses, and
 * treating them as IR-eligible would let a manager park a player who is merely likely to
 * miss one game and free a roster spot for it. The server used to allow Doubtful; it no
 * longer does.
 *
 * Unrecognised codes are NOT eligible. Fail closed: a designation we have never seen is not
 * a reason to open a roster spot.
 *
 * ⚠️ MIRRORED in supabase/functions/_shared/nfl-availability.ts, because Deno cannot import
 * from this tree. nflAvailability.contract.test.ts pins the two copies together.
 */

/** Roster states (`nfl_players.status`) that qualify. Compared case-insensitively. */
export const IR_ELIGIBLE_STATUS = ['IR', 'IRD', 'PUP', 'NON', 'EXE', 'SUS'] as const;

/**
 * Weekly injury-report values (`nfl_players.injury_status`) that qualify.
 * 'Injured Reserve' is here because the feed has never run and the historical maps in this
 * codebase all carried it; it costs nothing and fails closed if it never appears.
 */
export const IR_ELIGIBLE_INJURY = ['Out', 'IR', 'Injured Reserve'] as const;

export function isIREligible(
  injuryStatus: string | null | undefined,
  status: string | null | undefined,
): boolean {
  const roster = status?.trim().toUpperCase();
  if (roster && (IR_ELIGIBLE_STATUS as readonly string[]).includes(roster)) return true;
  const injury = injuryStatus?.trim().toLowerCase();
  if (injury && (IR_ELIGIBLE_INJURY as readonly string[]).some(v => v.toLowerCase() === injury)) return true;
  return false;
}
